import { QueryKey } from 'react-query';
import { MediaAPI, UsersAPI } from '../rest-endpoints';
import { serviceKey } from '../service';
import { IO } from '../types';

export const endpointKeys = {
  analyticsStats: [...serviceKey, 'analytics-stats'],
  formElements: [...serviceKey, 'form-elements'],
  formList: [...serviceKey, 'form-list'],
  formListV2: [...serviceKey, 'form-list-v2'],
  formListV3: [...serviceKey, 'form-list-v3'],
  formDetails: [...serviceKey, 'form-details'],
  formTemplate: [...serviceKey, 'form-template'],
  nonEditableForms: [...serviceKey, 'non-editable-forms'],
  eaglesoftMedicalHistoryForms: [...serviceKey, 'eaglesoft-medical-history-forms'],
  kioskForms: [...serviceKey, 'kiosk/forms'],
  kioskPackets: [...serviceKey, 'kiosk/packets'],
  medicalConditions: [...serviceKey, 'medical-conditions'],
  packetList: [...serviceKey, 'packet-list'],
  packetListV2: [...serviceKey, 'packet-list-v2'],
  packetListV3: [...serviceKey, 'packet-list-v3'],
  packetDetails: [...serviceKey, 'packet-details'],
  searchCustomUser: [...serviceKey, 'search-custom-user'],
  submissionsList: [...serviceKey, 'submissions-list'],
  templatesList: [...serviceKey, 'templates-list'],
  submissionPDF: [...serviceKey, 'submission-pdf'],
  submissionFormPDF: [...serviceKey, 'submission-form-pdf'],
  submissionAttachment: [...serviceKey, 'submission-attachment'],
  formPDF: [...serviceKey, 'form-pdf'],
  packetPDF: [...serviceKey, 'packet-pdf'],
  canUploadForms: [...serviceKey, 'can-upload-forms'],
  digitizationFeesCheck: [...serviceKey, 'digitization-fees-check'],
  formSubmitFlag: [...serviceKey, 'form-submit-flag'],
  usersWithFormsProviderReviewRole: [...serviceKey, 'users-with-forms-provider-review-role'],
  providerMappings: [...serviceKey, 'provider-mappings'],
  submissionDetailsForPerson: [...serviceKey, 'submission-details-for-person'],
  getUploadedFormsDetails: [...serviceKey, 'uploaded-forms-details'],
  getSubmissionDetailsById: [...serviceKey, 'submission-details-by-id'],
  getNPWritebackStatus: [...serviceKey, 'np-writeback-status'],
  writebackCapabilitiesV3: [...serviceKey, 'writeback-capabilities-v3'],
  sourceTenants: [...serviceKey, 'source-tenants'],
  bannerStatus: [...serviceKey, 'banner-status'],
  autoInsuranceVerificationStatus: [...serviceKey, 'auto-insurance-verification-status'],
};

export const requestKeys = {
  analyticsStats: (req: IO.GetAnalyticsStats['input'] & { type?: 'concise' | 'brief' }): QueryKey => [
    ...endpointKeys.analyticsStats,
    req,
  ],
  formElements: (req: IO.GetFormElements['input']): QueryKey => [...endpointKeys.formElements, req],
  formList: (req: IO.GetFormList['input']): QueryKey => [...endpointKeys.formList, req],
  formListV2: (req: IO.GetFormListV2['input']): QueryKey => [...endpointKeys.formListV2, req],
  formListV3: (req: IO.GetFormListV3['input']): QueryKey => [...endpointKeys.formListV3, req],
  formDetails: (req: IO.GetFormDetails['input']): QueryKey => [...endpointKeys.formDetails, req],
  formTemplate: (req: IO.GetFormTemplate['input']): QueryKey => [...endpointKeys.formTemplate, req],
  nonEditableForms: (req: IO.GetFormList['input']): QueryKey => [...endpointKeys.nonEditableForms, req],
  eaglesoftMedicalHistoryForms: (req: IO.GetFormList['input']): QueryKey => [
    ...endpointKeys.eaglesoftMedicalHistoryForms,
    req,
  ],
  kioskForms: (req: IO.KioskForms['input']): QueryKey => [...endpointKeys.kioskForms, req],
  kioskPackets: (req: IO.KioskPackets['input']): QueryKey => [...endpointKeys.kioskPackets, req],
  medicalConditions: (req: IO.GetPMSMedicalConditions['input']): QueryKey => [...endpointKeys.medicalConditions, req],
  packetList: (req: IO.GetPacketList['input']): QueryKey => [...endpointKeys.packetList, req],
  packetListV2: (req: IO.GetPacketListV2['input']): QueryKey => [...endpointKeys.packetList, req],
  packetListV3: (req: IO.GetPacketListV3['input']): QueryKey => [...endpointKeys.packetListV3, req],
  packetDetails: (req: IO.GetPacketDetails['input']): QueryKey => [...endpointKeys.packetDetails, req],
  searchCustomUser: (req: IO.SearchCustomUser['input']): QueryKey => [...endpointKeys.searchCustomUser, req],
  submissionsList: (req: IO.GetSubmissionsList['input']): QueryKey => [...endpointKeys.submissionsList, req],
  templatesList: (req: IO.TemplatesQueryRequest): QueryKey => [...endpointKeys.templatesList, req],
  submissionPDF: (req: IO.GetFormPDFAuthed['input']): QueryKey => [...endpointKeys.submissionPDF, req],
  submissionFormPDF: (req: IO.GetFormPDFAuthed['input']): QueryKey => [...endpointKeys.submissionFormPDF, req],
  submissionAttachment: (req: MediaAPI.FetchMediaIO['input']): QueryKey => [...endpointKeys.submissionAttachment, req],
  formPDF: (req: IO.GetFormPDFAuthed['input']): QueryKey => [...endpointKeys.formPDF, req],
  packetPDF: (req: IO.GetFormPDFAuthed['input']): QueryKey => [...endpointKeys.packetPDF, req],
  canUploadForms: (req: IO.CanUploadForms['input']): QueryKey => [...endpointKeys.canUploadForms, req],
  digitizationFeesCheck: (req: IO.GetDigitizationFeesCheck['input']): QueryKey => [
    ...endpointKeys.digitizationFeesCheck,
    req,
  ],
  formSubmitFlag: (req: IO.GetFormSubmitFlag['input']): QueryKey => [...endpointKeys.formSubmitFlag, req],
  usersWithFormsProviderReviewRole: (req: UsersAPI.FetchUsersWithFormsProviderReviewRoleIO['input']): QueryKey => [
    ...endpointKeys.usersWithFormsProviderReviewRole,
    req,
  ],
  providerMappings: (req: IO.GetProviderMappings['input']): QueryKey => [...endpointKeys.providerMappings, req],
  submissionDetailsForPerson: (req: IO.GetSubmissionDetailsForPerson['input']): QueryKey => [
    ...endpointKeys.submissionDetailsForPerson,
    req,
  ],
  getUploadedFormsDetails: (req: IO.GetUploadedFormsDetails['input']): QueryKey => [
    ...endpointKeys.getUploadedFormsDetails,
    req,
  ],
  getSubmissionDetailsById: (req: IO.GetSubmissionDetailsById['input']): QueryKey => [
    ...endpointKeys.getSubmissionDetailsById,
    req,
  ],
  getNPWritebackStatus: (req: IO.GetNPWritebackStatus['input']): QueryKey => [
    ...endpointKeys.getNPWritebackStatus,
    req,
  ],

  getWritebackCapabilitiesV3: (req: IO.GetWritebackCapabilitiesV3['input']): QueryKey => [
    ...endpointKeys.writebackCapabilitiesV3,
    req,
  ],
  getSourceTenants: (req: IO.GetSourceTenants['input']): QueryKey => [...endpointKeys.sourceTenants, req],

  getBannerStatus: (req: IO.GetBannerStatus['input']): QueryKey => [...endpointKeys.bannerStatus, req],

  getAutoInsuranceVerificationStatus: (req: IO.GetAutoInsuranceVerificationStatus['input']): QueryKey => [
    ...endpointKeys.autoInsuranceVerificationStatus,
    req,
  ],
};
